import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { AddressBookEntryService, AddressBookEntryUriBuilderParams } from '@app/core/services/address-book-entry.service';
import { SearchFilter } from '@app/shared/models/search-filter';
import { AddressBookEntry, AddressBookEntryList } from '@app/shared/models/address-book-entry';
import { ActionListBtnConfig, ActionListOption } from '@app/shared/components/action-list/action-list.component';
import { ContactTypes } from '@app/shared/models/contact';
import { TranslationEntry } from '@app/shared/models/translatable';
import { forkJoin } from 'rxjs';


@Component({
    templateUrl: './address-book-management.component.html',
    styleUrls: ['./address-book-management.component.scss']
})
export class AddressBookManagementComponent extends BaseWithBundleComponent
                                            implements OnInit, OnDestroy {

    public selected: AddressBookEntry;
    public actionListOptions: ActionListBtnConfig<AddressBookEntry>[];
    public list: AddressBookEntryList;

    public addressFilterProperties: TranslationEntry[] = [
        { key: 'name1', trans: marker('participant.name1') },
        { key: 'name2', trans: marker('participant.name2') },
        { key: 'name3', trans: marker('participant.name3') },
        { key: 'address_street', trans: marker('address.street') },
        { key: 'address_city', trans: marker('address.city') }
    ];

    public selectedAddressFilterProperty: TranslationEntry;
    public filter: SearchFilter<AddressBookEntry>;

    public massDeleteActive: boolean;
    public addressesToMassDelete: string[];

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected addressBookEntryService: AddressBookEntryService) {
        super(componentServiceBundle);
        this._idPrefix = 'settings-address-book-management.';
    }

    public ngOnInit() {
        super.ngOnInit();

        this.actionListOptions = [{
            option: ActionListOption.DELETE,
            order: 1
        }];

        this.filter = new SearchFilter<AddressBookEntry>(false, false, true,
            () => {
            this.fetchAddressBookEntryList();
        }, () => {
            // NO ACTION FOR TEXT CHANGE AS THIS HAPPENS ON BLUR
        });

        this.selectedAddressFilterProperty = this.addressFilterProperties.find(filterProp => filterProp.key === 'name1');
        this.fetchAddressBookEntryList();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
    }

    // ADDRESS METHODS ***************************************************************d*****************************************************

    public fetchAddressBookEntryList(idToSelect?: string): void {
        const queryParams: any = {
            limit: this.filter.letter || this.filter.searchText ? 50 : 0
        };

        if (this.filter.searchText || this.filter.letter) {
            queryParams.search = (this.filter.searchText || ``) +
                                 (this.filter.searchText && this.filter.letter ? `,` : ``) +
                                 (this.filter.letter ? `${this.selectedAddressFilterProperty.key}:^${this.filter.letter}` : ``);
        }

        this.addressBookEntryService.query(this.generateUriBuilderParams(), { queryParams: queryParams })
                                    .subscribe((result: AddressBookEntryList) => {
                this.list = result;
                this.list.items.sort(AddressBookEntry.sortByActionListDisplayName);
                this.filter.array = result.items;

                if (idToSelect) {
                    this.selectAddressBookEntry(this.list.getItem(idToSelect));
                } else if (this.filter.filteredArray && this.filter.filteredArray.length > 0) {
                    this.selectAddressBookEntry(this.list.getItem((<AddressBookEntry>this.filter.filteredArray[0]).id));
                }
            }, err => this.handleError(err));
    }

    public clearSearchAndFetch(): void {
        this.filter.searchText = '';
        this.fetchAddressBookEntryList();
    }

    public createNewAddressBookEntry(): void {
        this.selected = new AddressBookEntry({
            address: {
                contacts: [
                    { name: this.translateService.instant(ContactTypes.EMAIL.trans), value: '' },
                    { name: this.translateService.instant(ContactTypes.FAX.trans), value: '' },
                    { name: this.translateService.instant(ContactTypes.PHONE.trans), value: '' }
                ]
            }
        });
    }

    public selectAddressBookEntry(entry: AddressBookEntry): void {
        if (this.massDeleteActive) {
            this.toggleAddressBookEntryForMassDelete(entry);
        } else if (entry) {
            this.selected = null;
            this.addressBookEntryService.getById(this.generateUriBuilderParams(entry))
                .subscribe(result => {
                    this.selected = result;
                }, err => this.handleError(err));
        }
    }

    public deleteAddressBookEntry(entry: AddressBookEntry): void {
        this.addressBookEntryService.delete(this.generateUriBuilderParams(entry))
                                    .subscribe(result => {
            this.showSimpleConfirmSnackbar(marker('address_book_management.message.address_book_entry_deleted'));
            this.list.removeItem(entry);
            this.filter.array = this.list.items;
            
            if (this.selected && this.selected.id === entry.id) {
                this.selected = null;
            }
        }, err => this.handleError(err));
    }

    public updateInList(): void {
        if (this.list.getItem(this.selected.id)) {
            this.showSimpleConfirmSnackbar(marker('address_book_management.message.address_book_entry_updated'));
            this.list.replaceItem(this.selected);
        } else {
            this.showSimpleConfirmSnackbar(marker('address_book_management.message.address_book_entry_created'));
            this.list.addItem(this.selected);
        }

        this.filter.filter();
    }

    public cancel(): void {
        this.selected = null;
    }

    public toggleMassDelete(): void {
        this.massDeleteActive = !this.massDeleteActive;
        this.addressesToMassDelete = [];
        this.selected = null;
    }

    public selectedForDeletion(entry: AddressBookEntry): boolean {
        return entry ? !!this.addressesToMassDelete.find(idToDelete => idToDelete === entry.id) : false;
    }

    public toggleAddressBookEntryForMassDelete(entry: AddressBookEntry): void {
        const scheduledForDeletion = this.selectedForDeletion(entry);

        if (scheduledForDeletion) {
            this.addressesToMassDelete = this.addressesToMassDelete.filter(idToDelete => idToDelete !== entry.id);
        } else if (entry) {
            this.addressesToMassDelete.push(entry.id);
        }
    }

    public confirmMassDelete(): void {
        this.showSimpleConfirmDialog(
            marker('address_book_management.mass_delete.confirm_dialog_title'),
            this.translateService.instant(marker('address_book_management.mass_delete.confirm_dialog_text'),
                { amount: this.addressesToMassDelete.length }))
            .subscribe(confirmed => {
                if (confirmed) {
                    const observables = this.addressesToMassDelete.map(toDeleteId => {
                        return this.addressBookEntryService.delete(new AddressBookEntryUriBuilderParams(toDeleteId));
                    });

                    forkJoin(observables).subscribe((results: any) => {
                        this.showSimpleConfirmSnackbar(marker('address_book_management.mass_delete.success_message'));
                        this.list.items = this.list.items.filter(entry => {
                                                              return this.addressesToMassDelete.indexOf(entry.id) === -1;
                                                          });
                        this.filter.array = this.list.items;
                        this.toggleMassDelete();
                    }, err => {
                        this.handleError(err);
                    });
                }
            });
    }

    protected generateUriBuilderParams(entry?: AddressBookEntry): AddressBookEntryUriBuilderParams {
        return new AddressBookEntryUriBuilderParams(entry);
    }
}
